import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";

// CSV: portfolio_code,counterparty_name,counterparty_type,exposure_pct,as_of_date[,notes]
// counterparty_type: custodian | platform | derivative | other
export default function CounterpartyExposuresUploader() {
  const [csv, setCsv] = useState("");
  const [busy, setBusy] = useState(false);
  const [count, setCount] = useState(0);
  const { toast } = useToast();

  const loadStats = async () => {
    const { count } = await supabase.from("policy_counterparty_exposures" as any).select("*", { count: "exact", head: true });
    setCount(count ?? 0);
  };
  useEffect(() => { loadStats(); }, []);

  const upload = async () => {
    setBusy(true);
    try {
      const lines = csv.trim().split(/\r?\n/).filter(l => l.trim());
      if (!lines.length) throw new Error("No rows.");
      const data = lines[0].toLowerCase().includes("portfolio_code") ? lines.slice(1) : lines;

      const rows = data.map(l => {
        const [portfolio_code, counterparty_name, counterparty_type, expRaw, as_of_date, ...rest] = l.split(",").map(s => s.trim());
        const exposure_pct = Number(expRaw);
        if (!portfolio_code || !counterparty_name || !counterparty_type || !as_of_date || !Number.isFinite(exposure_pct)) {
          throw new Error(`Bad row: ${l}`);
        }
        return { portfolio_code, counterparty_name, counterparty_type, exposure_pct, as_of_date, notes: rest.join(",") || null };
      });

      const { error } = await supabase
        .from("policy_counterparty_exposures" as any)
        .upsert(rows, { onConflict: "portfolio_code,counterparty_name,counterparty_type,as_of_date" });
      if (error) throw error;

      toast({ title: "Upload complete", description: `${rows.length} exposures upserted.` });
      setCsv("");
      loadStats();
    } catch (e: any) {
      toast({ title: "Upload failed", description: e.message ?? String(e), variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader><CardTitle>§15 Counterparty Exposures</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="text-xs text-muted-foreground space-y-1">
          <p>Per-portfolio exposure to custodians, platforms, and derivative counterparties. Powers §15 concentration limit checks.</p>
          <p><strong>CSV:</strong> <code>portfolio_code,counterparty_name,counterparty_type,exposure_pct,as_of_date[,notes]</code></p>
          <p><strong>counterparty_type:</strong> <code>custodian | platform | derivative | other</code>. <strong>exposure_pct</strong> as percentage 0–100.</p>
        </div>
        <div className="text-xs border rounded p-3 bg-muted/30">
          <span className="text-muted-foreground">Exposure rows:</span>{" "}
          <span className="font-medium tabular-nums">{count.toLocaleString()}</span>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Paste CSV</Label>
          <Textarea
            value={csv}
            onChange={(e) => setCsv(e.target.value)}
            placeholder={"portfolio_code,counterparty_name,counterparty_type,exposure_pct,as_of_date,notes\nP3,Transact,platform,100,2026-05-15,"}
            rows={8}
            className="font-mono text-xs"
          />
        </div>
        <div className="flex gap-2">
          <Button onClick={upload} disabled={busy || !csv.trim()}>{busy ? "Uploading…" : "Upload"}</Button>
          <Button variant="outline" onClick={() => setCsv("")} disabled={busy}>Clear</Button>
        </div>
      </CardContent>
    </Card>
  );
}
